import { useMemo, useState } from 'react';

import { useEvent } from '@mint-lib/hooks';

import {
  ComboboxInput,
  ComboboxInputHelperText,
  ComboboxInputLabel,
  ComboboxInputRoot,
  ComboboxMenu,
  ComboboxMenuButton,
} from '../index.js';
import { ComboboxMultipleContext } from './ComboboxMultipleContext.jsx';

export type MultiOption = {
  label: string;
  value: string;
  disabled?: boolean;
  message?: string;
};

type ComboboxTextLabels = {
  selectAllText?: string;
  clearAllText?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  placeholder?: string;
};

type MultiSelectProps = {
  placeholder: string;
  options: MultiOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
  readOnly?: boolean;
  withSearch?: boolean;
  disabled?: boolean;
  label: string;
  id: string;
  infoText?: string;
  helperText?: string;
  color?: 'default' | 'warning' | 'error';
  InputProps?: React.ComponentProps<typeof ComboboxInput>;
  size?: 'small' | 'medium';
  className?: string;
  isLoading?: boolean;
  children?: React.ReactNode;
  textLabels?: ComboboxTextLabels;
};

type BaseProps = Omit<
  MultiSelectProps,
  'searchValue' | 'onSearchChange' | 'withSearch'
>;

export type ComboboxMultipleProps =
  | (BaseProps & {
      withSearch: true;
      onSearchChange: (val: string) => void;
    })
  | (BaseProps & { withSearch?: false });

function ComboboxMultiple(props: ComboboxMultipleProps) {
  const {
    placeholder,
    options,
    value = [],
    onChange,
    withSearch,
    label,
    id,
    readOnly,
    infoText,
    disabled,
    helperText,
    InputProps,
    size,
    className,
    isLoading,
    color,
    textLabels,
    children,
  } = props;
  const onSearchChange = withSearch ? props.onSearchChange : undefined;
  const [isOpen, setIsOpen] = useState(false);

  const displayValue = useMemo(() => {
    if (!value || value.length === 0) return '';
    if (!options) return '';

    const selectedOptions = options.filter((o) => value.includes(o.value));
    return selectedOptions.map((o) => o.label).join(', ');
  }, [options, value]);

  const handleSelect = useEvent((val: string) => {
    if (readOnly || !onChange) return;

    const isSelected = value.includes(val);
    const newValue = isSelected
      ? value.filter((v) => v !== val)
      : [...value, val];

    const isDisabled = options?.find((o) => o.value === val)?.disabled;
    if (isDisabled) return;

    onChange(newValue);
  });

  const handleSelectAll = useEvent(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();
      if (readOnly || !onChange) return;
      if (!options) return;

      const isAllSelected =
        options.length > 0 && value.length === options.length;

      const disabledSelected = options.filter(
        (o) => o.disabled && value.includes(o.value),
      );
      if (isAllSelected) {
        const disabledValues = disabledSelected.map((o) => o.value);
        onChange(disabledValues);
      } else {
        const allValues = options.map((o) => o.value);
        onChange(allValues);
      }
    },
  );

  const handleOpenChange = useEvent((open: boolean) => {
    if (readOnly) return;
    setIsOpen(open);
  });

  const context = useMemo(
    () => ({ value, onSelect: handleSelect, color }),
    [value, handleSelect, color],
  );

  return (
    <ComboboxInputRoot className={className}>
      <ComboboxInputLabel
        label={label}
        htmlFor={id}
        infoIconWithTooltip={infoText}
      />
      <ComboboxInput
        popoverClassName="z-popover"
        value={displayValue}
        placeholder={placeholder}
        open={isOpen && !readOnly}
        disabled={disabled}
        onOpenChange={handleOpenChange}
        readOnly={readOnly}
        size={size}
        color={color}
        inputProps={{
          id,
          ...InputProps,
        }}
      >
        <ComboboxMenu
          shouldFilter={false}
          withSearch={withSearch}
          onValueChange={onSearchChange}
          placeholder={textLabels?.placeholder || 'Search'}
          emptyMessage={
            isLoading
              ? textLabels?.loadingMessage || 'Loading…'
              : textLabels?.emptyMessage || 'No options.'
          }
        >
          {options && options.length > 0 && (
            <ComboboxMenuButton
              selectedOptions={value}
              options={options}
              onSelectAll={handleSelectAll}
              onClearAll={handleSelectAll}
              selectAllText={textLabels?.selectAllText || 'Select all'}
              clearAllText={textLabels?.clearAllText || 'Clear all'}
            />
          )}
          <ComboboxMultipleContext.Provider value={context}>
            {children}
          </ComboboxMultipleContext.Provider>
        </ComboboxMenu>
      </ComboboxInput>
      {helperText ? (
        <ComboboxInputHelperText helperText={helperText} color={color} />
      ) : null}
    </ComboboxInputRoot>
  );
}

export default ComboboxMultiple;
